import React, { useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Upload, Calendar, CheckCircle } from "lucide-react";
import FormItems from "./FormItems";
import SupplierItems from "./SupplierItems";
import { API_ENDPOINT } from "../../utils/constants";
import useApiEp from "../../hooks/useApiEp";
import { getHeader } from "../../utils/utils2";
import useAdminEventSettings from "../../hooks/useAdminEventSettings";

const SUPPLIER_DISABLED_MESSAGE =
  "The Supplier option will be activated once Vendor Management Committee approves the final list.";

const OesDataForm = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const { data: formItems } = useApiEp(`/admin/get-form-items`);
  const { data: eventSettings, isLoading: eventSettingsLoading } =
    useAdminEventSettings();

  const isFormItemsEmpty = !formItems || formItems.length === 0;
  const supplierReady = eventSettings?.supplierReady === true;

  const events = [
    {
      id: "imtex2025",
      name: "IMTEX 2025",
      description: " innovations in manufacturing technology",
    },
    {
      id: "dmtx2025-imtma",
      name: "DMTX 2025 - IMTMA",
      description: "Delhi Machine Tool Expo 2025",
    },
    {
      id: "imtex2026-imtma",
      name: "IMTEX 2026 - IMTMA",
      description: "IMTEX 2026",
    },
  ];

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleImportClick = () => {
    setShowImportDialog(true);
    setSelectedEvent(null);
    setImportResult(null);
  };

  const handleEventSelect = (eventId) => {
    setSelectedEvent(eventId);
  };

  const handleImportConfirm = async () => {
    if (!selectedEvent) return;

    setLoading(true);
    setImportResult(null);

    try {
      const response = await fetch(`${API_ENDPOINT}/admin/import-oes-forms`, {
        method: "POST",
        ...getHeader(),
        body: JSON.stringify({ eventId: selectedEvent }),
      });

      const data = await response.json();

      if (response.ok) {
        setImportResult({
          success: true,
          message: data.message || "Import successful!",
        });
        setTimeout(() => {
          setShowImportDialog(false);
          setImportResult(null);
          document.location.reload();
        }, 2000);
      } else {
        setImportResult({
          success: false,
          message: data.error || "Import failed",
        });
      }
    } catch (error) {
      setImportResult({
        success: false,
        message: "Network error. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f6f8fc",
          color: "#0f172a",
          px: { xs: 2, sm: 3, lg: 4 },
          py: { xs: 2, sm: 3 },
        }}
      >
        <Box maxWidth={1440} mx="auto">
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: "1.65rem", sm: "2rem" },
                  lineHeight: 1.2,
                  fontWeight: 700,
                  letterSpacing: "-0.025em",
                }}
              >
                Form Names &amp; Suppliers
              </Typography>
              <Typography
                sx={{ mt: 0.75, color: "#64748b", fontSize: "0.9rem" }}
              >
                Manage OES forms, expiry dates, field configurations, and
                supplier entries.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              disabled={!isFormItemsEmpty}
              startIcon={<Upload size={20} />}
              onClick={handleImportClick}
              sx={{
                flexShrink: 0,
                minHeight: 42,
                px: 2,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 600,
                borderColor: "#c4b5fd",
                color: "#7c3aed",
                bgcolor: "#fff",
                "&:hover": {
                  borderColor: "#7c3aed",
                  bgcolor: "#faf5ff",
                },
              }}
            >
              Import from Previous Events
            </Button>
          </Box>

          {tabIndex === 1 && !eventSettingsLoading && !supplierReady && (
            <Alert
              id="supplier-ready-message"
              severity="warning"
              sx={{ mb: 2.5, border: "1px solid #f6cf75" }}
            >
              {SUPPLIER_DISABLED_MESSAGE}
            </Alert>
          )}

          <Box
            sx={{
              bgcolor: "#fff",
              border: "1px solid #dbe3ef",
              borderRadius: 2,
              px: { xs: 1, sm: 2 },
              mb: 3,
            }}
          >
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              aria-label="Form names and suppliers"
              sx={{
                minHeight: 56,
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: "4px 4px 0 0",
                  bgcolor: "#7c3aed",
                },
                "& .MuiTab-root": {
                  minHeight: 56,
                  px: { xs: 1.75, sm: 2.5 },
                  textTransform: "none",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "#64748b",
                },
                "& .Mui-selected": { color: "#7c3aed !important" },
              }}
            >
              <Tab label="Form Names" />
              <Tab label="Suppliers" />
            </Tabs>
          </Box>

          <Box>
            {tabIndex === 0 && <FormItems />}
            {tabIndex === 1 && <SupplierItems supplierReady={supplierReady} />}
          </Box>

          <Dialog
            open={showImportDialog}
            onClose={() => !loading && setShowImportDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Upload size={24} />
                Import from Previous Events
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select an event to import form names and suppliers from:
              </Typography>

              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Warning:</strong> This action will replace the current
                  form names and suppliers with those from the selected event.
                  Existing data cannot be recovered.
                </Typography>
              </Alert>

              {importResult && (
                <Alert
                  severity={importResult.success ? "success" : "error"}
                  sx={{ mb: 2 }}
                >
                  {importResult.message}
                </Alert>
              )}

              <Grid container spacing={2}>
                {events.map((event) => (
                  <Grid item xs={12} key={event.id}>
                    <Card
                      variant={
                        selectedEvent === event.id ? "outlined" : "elevation"
                      }
                      sx={{
                        border:
                          selectedEvent === event.id
                            ? "2px solid #1976d2"
                            : "none",
                        backgroundColor:
                          selectedEvent === event.id ? "#e3f2fd" : "inherit",
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleEventSelect(event.id)}
                        disabled={loading}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Calendar size={20} />
                              <Typography variant="h6">{event.name}</Typography>
                            </Box>
                            {selectedEvent === event.id && (
                              <CheckCircle size={20} color="#1976d2" />
                            )}
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            {event.description}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => setShowImportDialog(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleImportConfirm}
                variant="contained"
                disabled={!selectedEvent || loading}
                startIcon={loading && <CircularProgress size={16} />}
              >
                {loading ? "Importing..." : "Import"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default OesDataForm;
